import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Receipt, CreditCard, AlertTriangle } from "lucide-react";
import { Container } from "@/components/ui-kit";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { fetchCustomerWebsiteBilling } from "@/lib/api";

function money(minor, currency) {
  const symbol = currency === "PHP" ? "₱" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${(minor / 100).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const INVOICE_STATUS_STYLE = {
  paid: "bg-[#10B981]/10 text-[#047857]",
  open: "bg-amber-50 text-amber-700",
  partially_paid: "bg-amber-50 text-amber-700",
  void: "bg-slate-100 text-slate-500",
};

export default function CustomerWebsiteBilling() {
  const { slug } = useParams();
  const { token, checking, isLoggedIn } = useCustomerAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    fetchCustomerWebsiteBilling(token, slug).then(setData).catch(() => setError("Could not load billing for this website."));
  }, [token, slug]);

  if (checking) return null;
  if (!isLoggedIn) return <Navigate to="/account/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Container className="py-12 max-w-4xl">
        <Link to="/account/dashboard" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#10B981] mb-6"><ArrowLeft size={15} /> Back to my websites</Link>
        <h1 className="text-2xl font-bold font-display text-[#0F172A]">{slug}</h1>

        {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
        {!data ? <p className="mt-6 text-[#475569]">Loading…</p> : (
          <>
            {data.subscription && (
              <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 text-[#0F172A] font-semibold"><CreditCard size={18} className="text-[#10B981]" /> Subscription</div>
                <p className="mt-2 text-sm text-[#475569]">Status: <span className="font-medium text-[#0F172A]">{data.subscription.status}</span></p>
                {data.subscription.next_billing_at && <p className="text-sm text-[#475569]">Next billing: {new Date(data.subscription.next_billing_at).toLocaleDateString()}</p>}
                {data.subscription.status === "past_due" && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-amber-700"><AlertTriangle size={14} /> Payment is past due.</p>
                )}
              </div>
            )}

            <div className="mt-6">
              <h2 className="flex items-center gap-2 text-lg font-bold font-display text-[#0F172A] mb-3"><Receipt size={18} className="text-[#10B981]" /> Invoices</h2>
              {data.invoices.length === 0 ? (
                <p className="text-sm text-[#475569]">No invoices yet.</p>
              ) : (
                <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
                  {data.invoices.map((inv) => (
                    <Link data-testid={`website-invoice-${inv.id}`} to={`/account/invoices/${inv.id}`} key={inv.id} className="flex items-center justify-between gap-4 border-b border-[#E2E8F0] last:border-0 px-5 py-4 hover:bg-slate-50">
                      <div>
                        <p className="font-medium text-[#0F172A] text-sm">{inv.invoice_number}</p>
                        <p className="text-xs text-[#94A3B8]">{new Date(inv.invoice_date).toLocaleDateString()} · {inv.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[#0F172A]">{money(inv.gross_amount_minor, inv.currency)}</p>
                        <span className={`inline-block mt-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${INVOICE_STATUS_STYLE[inv.status] || "bg-slate-100 text-slate-600"}`}>{inv.status}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold font-display text-[#0F172A] mb-3">Payment history</h2>
              {data.payments.length === 0 ? (
                <p className="text-sm text-[#475569]">No payments recorded yet.</p>
              ) : (
                <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
                  {data.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-4 border-b border-[#E2E8F0] last:border-0 px-5 py-4 text-sm">
                      <div>
                        <p className="text-[#0F172A]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-[#94A3B8] capitalize">{p.payment_method_type || p.provider}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#0F172A]">{money(p.gross_amount_minor, p.currency)}</p>
                        <p className="text-xs text-[#94A3B8] capitalize">{p.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
